/**
 * Photos of a job, before and after (M7, mobile assessment §G M7).
 *
 * The bytes go to the upload-booking-evidence Edge Function, which checks
 * them and records the row as the caller (add_booking_evidence); this module
 * never writes the table or the bucket itself. A photo is re-encoded through
 * stripImageMetadata first: it is taken inside a client's home, and the
 * function refuses one that still carries its location (HTTP 422).
 */
import { supabase } from '@/integrations/supabase/client';
import { stripImageMetadata } from '@/lib/imageMetadata';
import { invokeErrorDetail, isDefiniteAnswer, prepareUpload, settleUpload, UPLOAD_ALREADY_USED_MESSAGE } from '@/lib/uploadOperation';
import {
  configureUploadSession,
  configureUploadTarget,
  enqueueUpload,
  uploadOutboxGeneration,
  type SendResult,
} from '@/lib/uploadOutbox';
import { activeSession } from '@/lib/activeSessionUser';

export type JobPhotoKind = 'before' | 'after';

/** The bucket's limit (20270412000000_helper_journey_states.sql). */
export const JOB_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
/** The server's limit of photos of each kind per job (upload-booking-evidence). */
export const JOB_PHOTOS_PER_KIND = 5;
/** A camera photo is scaled to this long edge before the size check. */
export const JOB_PHOTO_MAX_EDGE = 2048;

export interface JobPhotoCounts {
  before: number;
  after: number;
}

const MESSAGES: Record<number, string> = {
  413: 'That photo is too large. The maximum size is 5 MB.',
  415: 'Job photos must be a JPG or PNG image.',
  422: 'That photo still contains private location or device metadata. Please take it again.',
};

/**
 * One attempt to store a cleaned photo under its operation id, classified:
 * `transient` when there was no definite answer (no response, a 5xx, the
 * 503 of another request holding the same operation, or the upload
 * ceiling's 429, which is answered before the body is read), so the same id
 * and bytes may be sent again; otherwise the answer stands.
 */
export async function sendJobPhoto(
  bookingId: string,
  kind: JobPhotoKind,
  operationId: string,
  cleaned: File,
  /** The outbox's confirmed owner's token; the website sends without one. */
  accessToken?: string,
): Promise<SendResult> {
  const body = new FormData();
  body.append('bookingId', bookingId);
  body.append('kind', kind);
  body.append('operationId', operationId);
  body.append('file', cleaned);
  const { data, error } = await supabase.functions.invoke(
    'upload-booking-evidence',
    accessToken ? { body, headers: { Authorization: `Bearer ${accessToken}` } } : { body },
  );
  if (error) {
    const { status, message, code } = await invokeErrorDetail(error);
    if (code === 'KB001') return { ok: false, transient: false, message: UPLOAD_ALREADY_USED_MESSAGE };
    // 409 carries the reason in the server's own words (five already, or not
    // the right moment in the job); the others have fixed meanings.
    const text = (status && MESSAGES[status]) || message || 'Could not upload that photo. Please try again.';
    // isDefiniteAnswer: a 429 is not one (Codex on #1221). Settling on it let
    // a retry of an upload whose first answer was lost send a NEW id once the
    // window reset, and store the photo twice.
    return { ok: false, transient: !isDefiniteAnswer(status), message: text, status };
  }
  const payload = data as { error?: string } | null;
  if (payload?.error) return { ok: false, transient: false, message: payload.error };
  return { ok: true };
}

/** Cleans a picked photo once and fixes its operation id (uploadOperation.ts). */
async function prepareJobPhoto(bookingId: string, kind: JobPhotoKind, file: File) {
  const prepared = await prepareUpload(file, `${bookingId}:${kind}`, (f) =>
    stripImageMetadata(f, { maxEdge: JOB_PHOTO_MAX_EDGE }));
  if (prepared.cleaned.size > JOB_PHOTO_MAX_BYTES) {
    settleUpload(file);
    throw new Error(MESSAGES[413]);
  }
  return prepared;
}

/**
 * Uploads one photo of the job. Throws an Error whose message a helper can act on.
 *
 * M10: a retry with the same file sends the same operation id and bytes
 * (uploadOperation.ts), so an upload whose answer was lost comes back as a
 * success rather than a second photo.
 */
export async function uploadJobPhoto(bookingId: string, kind: JobPhotoKind, file: File): Promise<void> {
  const { operationId, cleaned } = await prepareJobPhoto(bookingId, kind, file);
  const result = await sendJobPhoto(bookingId, kind, operationId, cleaned);
  // A definite answer: the next try is a new upload. A lost or failed
  // request keeps its id, so the retry can be recognised.
  if (result.ok || !result.transient) settleUpload(file);
  if (!result.ok) throw new Error(result.message);
}

/**
 * App only (M10): keep the photo, encrypted, and upload it from the outbox,
 * now or when the connection returns, even after a force-stop. Resolves once
 * it is kept, not once it is uploaded: the caller says so.
 */
export async function queueJobPhoto(ownerId: string, bookingId: string, kind: JobPhotoKind, file: File): Promise<void> {
  // Taken before the photo is hashed and re-encoded: a sign-out during that
  // work must refuse it, not hand it to the next session (Codex on #1137).
  const since = uploadOutboxGeneration();
  const { operationId, cleaned } = await prepareJobPhoto(bookingId, kind, file);
  try {
    await enqueueUpload({
      id: operationId,
      ownerId,
      target: 'booking-evidence',
      fields: { bookingId, kind },
      file: cleaned,
      since,
    });
  } finally {
    // Kept: the outbox holds the id and the bytes from here on. Not kept:
    // nothing was sent with this id, so the prepared photo is released, not
    // held in memory past a sign-out (Codex on #1137).
    settleUpload(file);
  }
}

const RESEND_STATUSES = new Set([401, 408, 429]);

configureUploadSession(activeSession);
configureUploadTarget('booking-evidence', async (entry, file, accessToken) => {
  const result = await sendJobPhoto(entry.fields.bookingId, entry.fields.kind as JobPhotoKind, entry.id, file, accessToken);
  // Not the photo's fault, so sent again with backoff rather than shown as
  // refused: a token that expired while the photo waited (401), a request
  // that timed out (408), and throttling (429). The operation id makes the
  // resend safe (Codex on #1137).
  return !result.ok && result.status !== undefined && RESEND_STATUSES.has(result.status)
    ? { ...result, transient: true }
    : result;
});

export async function fetchJobPhotoCounts(bookingId: string): Promise<JobPhotoCounts> {
  const { data, error } = await supabase
    .from('booking_evidence')
    .select('kind')
    .eq('booking_id', bookingId);
  if (error) throw error;
  const counts: JobPhotoCounts = { before: 0, after: 0 };
  for (const row of data ?? []) {
    if (row.kind === 'before' || row.kind === 'after') counts[row.kind] += 1;
  }
  return counts;
}
